import { useCallback, useMemo, useRef, useState, useEffect } from 'react'

import { GridPaginationMeta, GridPaginationModel } from '@mui/x-data-grid'

import { DEFAULT_PAGE_LIMIT } from 'app/lib/constants'
import { usePaginationDisplayText } from 'app/lib/hooks/use-pagination-display-text'

import { BasePaginatedTable, BasePaginatedTableProps } from './BasePaginatedTable'

interface CursorPaginationProps {
  nextCursor?: string | null
  prevCursor?: string | null
}

interface CursorPaginationGridProps
  extends Pick<
    BasePaginatedTableProps,
    'rows' | 'columns' | 'loading' | 'onRowClick' | 'hideColumnHeaders'
  > {
  limit?: number
  pagination: CursorPaginationProps
  onFetchData: (cursor?: string) => void
  testID?: string
}

export const CursorPaginatedTable = ({
  rows,
  columns,
  limit = DEFAULT_PAGE_LIMIT,
  pagination,
  onFetchData,
  onRowClick,
  loading = false,
  hideColumnHeaders,
  testID = 'cursor-paginated-table',
}: CursorPaginationGridProps) => {
  const [rowCountState, setRowCountState] = useState(0)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: limit,
  })

  const updatePaginationAndFetch = useCallback(
    (newModel: GridPaginationModel, cursor?: string) => {
      setPaginationModel(newModel)
      onFetchData(cursor)
    },
    [onFetchData],
  )

  const handlePaginationModelChange = useCallback(
    (newPaginationModel: GridPaginationModel) => {
      const isGoingForward = newPaginationModel.page > paginationModel.page
      const isGoingBackward = newPaginationModel.page < paginationModel.page
      const isFirstPage = newPaginationModel.page === 0

      if (isFirstPage) {
        updatePaginationAndFetch(newPaginationModel, undefined)
      } else if (isGoingForward && pagination.nextCursor) {
        updatePaginationAndFetch(newPaginationModel, pagination.nextCursor)
      } else if (isGoingBackward && pagination.prevCursor) {
        updatePaginationAndFetch(newPaginationModel, pagination.prevCursor)
      }
    },
    [paginationModel.page, pagination.nextCursor, pagination.prevCursor, updatePaginationAndFetch],
  )

  const paginationMetaRef = useRef<GridPaginationMeta>(undefined)

  const hasNextPage = !!pagination.nextCursor

  const paginationMeta = useMemo(() => {
    if (hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage) {
      paginationMetaRef.current = { hasNextPage }
    }
    return paginationMetaRef.current
  }, [hasNextPage])

  const paginationDisplayText = usePaginationDisplayText({ countUnknown: hasNextPage })

  useEffect(() => {
    if (paginationMeta?.hasNextPage !== false) {
      setRowCountState(-1)
    }
  }, [paginationMeta?.hasNextPage])

  return (
    <BasePaginatedTable
      rows={rows}
      columns={columns}
      loading={loading}
      onRowClick={onRowClick}
      hideColumnHeaders={hideColumnHeaders}
      testID={testID}
      paginationModel={paginationModel}
      onPaginationModelChange={handlePaginationModelChange}
      rowCount={rowCountState}
      onRowCountChange={(newRowCount: number) => setRowCountState(newRowCount)}
      paginationMeta={paginationMeta}
      paginationDisplayText={paginationDisplayText}
      pageSizeOptions={[limit]}
    />
  )
}
